import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

export default function TermsOfService() {
  const navigate = useNavigate();

  // Auth pages always render in dark mode
  useEffect(() => {
    document.documentElement.classList.add('dark');
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 px-4 py-12">
      <div className="w-full max-w-3xl mx-auto">
        {/* Logo / Brand */}
        <div className="text-center mb-8">
          <Link to="/" className="text-3xl font-bold text-gray-900 dark:text-white hover:text-blue-500 dark:hover:text-blue-400 transition-colors" style={{ fontFamily: "'Orbitron', sans-serif" }}>
            CREW FORGE
          </Link>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Terms of Service
          </p>
        </div>

        {/* Back link */}
        <div className="mb-4">
          <button
            onClick={() => navigate(-1)}
            className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
          >
            &larr; Back
          </button>
        </div>

        {/* Content Card */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6 sm:p-8">
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            Last updated: 11 April 2026
          </p>

          {/* 1. Who We Are */}
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mt-6 mb-2">
            1. Who We Are
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 leading-relaxed">
            Crew Forge is operated by <strong className="text-gray-900 dark:text-white">Engineering Effects Ltd</strong> (Company No. pending registration), a company registered in England and Wales ("we", "us", "our").
          </p>

          {/* 2. Acceptance of Terms */}
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mt-6 mb-2">
            2. Acceptance of Terms
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 leading-relaxed">
            By creating an account, accepting an invitation, or using Crew Forge, you agree to these Terms of Service. If you do not agree, you must not use the platform.
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 leading-relaxed">
            These terms form a legally binding agreement between you and Engineering Effects Ltd.
          </p>

          {/* 3. About the Service */}
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mt-6 mb-2">
            3. About the Service
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 leading-relaxed">
            Crew Forge is a web-based platform for film and television production teams to manage:
          </p>
          <ul className="text-sm text-gray-600 dark:text-gray-300 mb-3 leading-relaxed list-disc list-inside space-y-1 ml-2">
            <li>Orders and requisitions</li>
            <li>Approvals</li>
            <li>Budgeting and spend tracking</li>
            <li>Supplier and equipment hire tracking</li>
          </ul>
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 leading-relaxed">
            The service is intended for business and professional use only.
          </p>

          {/* 4. User Accounts */}
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mt-6 mb-2">
            4. User Accounts
          </h2>
          <ul className="text-sm text-gray-600 dark:text-gray-300 mb-3 leading-relaxed list-disc list-inside space-y-1 ml-2">
            <li>Access is by invitation only</li>
            <li>Accounts must not be shared</li>
            <li>You are responsible for all activity under your account</li>
            <li>You must notify us of unauthorised access</li>
          </ul>
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 leading-relaxed">
            We may:
          </p>
          <ul className="text-sm text-gray-600 dark:text-gray-300 mb-3 leading-relaxed list-disc list-inside space-y-1 ml-2">
            <li>Require password resets</li>
            <li>Suspend access for security reasons</li>
          </ul>
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 leading-relaxed">
            You agree to provide accurate and up-to-date information.
          </p>

          {/* 5. Acceptable Use */}
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mt-6 mb-2">
            4. Acceptable Use
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 leading-relaxed">
            You agree not to:
          </p>
          <ul className="text-sm text-gray-600 dark:text-gray-300 mb-3 leading-relaxed list-disc list-inside space-y-1 ml-2">
            <li>Use the platform unlawfully</li>
            <li>Submit false or misleading data</li>
            <li>Attempt unauthorised access</li>
            <li>Disrupt or overload systems</li>
            <li>Upload malicious or harmful content</li>
            <li>Scrape or extract data without permission</li>
            <li>Use the platform outside its intended purpose</li>
          </ul>

          {/* 5. Intellectual Property */}
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mt-6 mb-2">
            6. Intellectual Property
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 leading-relaxed">
            All intellectual property in Crew Forge, including software, design, branding, and code, belongs to Engineering Effects Ltd.
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 leading-relaxed">
            You retain ownership of any data you upload.
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 leading-relaxed">
            You grant us a limited licence to host, store, process, transmit, and display data to authorised users — solely to provide the service.
          </p>

          {/* 7. Privacy */}
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mt-6 mb-2">
            7. Privacy
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 leading-relaxed">
            Use of the platform is governed by our{' '}
            <Link to="/privacy-policy" className="text-blue-600 dark:text-blue-400 hover:underline">
              Privacy Policy
            </Link>
            .
          </p>

          {/* 8. Service Availability */}
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mt-6 mb-2">
            8. Service Availability
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 leading-relaxed">
            The service is provided "as is" and "as available."
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 leading-relaxed">
            We do not guarantee uptime.
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 leading-relaxed">
            We may modify or suspend the service, but will use reasonable efforts to provide notice for material changes unless urgent.
          </p>

          {/* 9. Limitation of Liability */}
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mt-6 mb-2">
            9. Limitation of Liability
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 leading-relaxed">
            To the fullest extent permitted by law:
          </p>
          <ul className="text-sm text-gray-600 dark:text-gray-300 mb-3 leading-relaxed list-disc list-inside space-y-1 ml-2">
            <li>We are not liable for indirect or consequential losses</li>
            <li>We are not liable for loss of profits, data, or business opportunities</li>
            <li>Our total liability is limited to fees paid in the previous 12 months (or £100 where no fees have been paid)</li>
          </ul>
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 leading-relaxed">
            Nothing excludes liability for:
          </p>
          <ul className="text-sm text-gray-600 dark:text-gray-300 mb-3 leading-relaxed list-disc list-inside space-y-1 ml-2">
            <li>Death or personal injury caused by negligence</li>
            <li>Fraud or fraudulent misrepresentation</li>
            <li>Any liability that cannot be excluded by law</li>
          </ul>

          {/* 10. Suspension and Termination */}
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mt-6 mb-2">
            10. Suspension and Termination
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 leading-relaxed">
            We may suspend or terminate access where:
          </p>
          <ul className="text-sm text-gray-600 dark:text-gray-300 mb-3 leading-relaxed list-disc list-inside space-y-1 ml-2">
            <li>These terms are breached</li>
            <li>Security risks arise</li>
            <li>Misuse is suspected</li>
            <li>Required by law</li>
          </ul>
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 leading-relaxed">
            Department administrators may deactivate user accounts.
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 leading-relaxed">
            Upon termination, access ends immediately. Data may be retained in accordance with our{' '}
            <Link to="/privacy-policy" className="text-blue-600 dark:text-blue-400 hover:underline">Privacy Policy</Link>.
          </p>

          {/* 11. Data Retention and Deletion */}
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mt-6 mb-2">
            11. Data Retention and Deletion
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 leading-relaxed">
            We may retain data where required for:
          </p>
          <ul className="text-sm text-gray-600 dark:text-gray-300 mb-3 leading-relaxed list-disc list-inside space-y-1 ml-2">
            <li>Legal obligations</li>
            <li>Audit and accounting</li>
            <li>Security and fraud prevention</li>
          </ul>
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 leading-relaxed">
            You may request deletion of personal data subject to these requirements.
            Deletion requests are actioned within 30 days per UK GDPR Article 12(3).
            To request deletion, email{' '}
            <a href="mailto:support@crewforge.co.uk?subject=Account%20deletion%20request" className="text-blue-600 dark:text-blue-400 hover:underline">
              support@crewforge.co.uk
            </a>{' '}
            or use the Delete Your Account section on <strong>My Settings</strong>.
            See our{' '}
            <Link to="/privacy-policy" className="text-blue-600 dark:text-blue-400 hover:underline">
              Privacy Policy
            </Link>
            {' '}Section 7 for full details of what is scrubbed and what is retained.
          </p>

          {/* 12. Changes to Terms */}
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mt-6 mb-2">
            12. Changes to Terms
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 leading-relaxed">
            We may update these terms. Material changes will be notified where reasonably possible. Continued use constitutes acceptance.
          </p>

          {/* 13. Governing Law */}
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mt-6 mb-2">
            13. Governing Law
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 leading-relaxed">
            These terms are governed by the laws of England and Wales. Any disputes are subject to the exclusive jurisdiction of the courts of England and Wales.
          </p>

          {/* 14. Contact */}
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mt-6 mb-2">
            14. Contact
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 leading-relaxed">
            Contact:{' '}
            <a href="mailto:support@crewforge.co.uk" className="text-blue-600 dark:text-blue-400 hover:underline">
              support@crewforge.co.uk
            </a>
          </p>
        </div>

        {/* Footer links */}
        <p className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400">
          <Link to="/privacy-policy" className="text-blue-600 dark:text-blue-400 hover:underline">
            Privacy Policy
          </Link>
          {' '}&middot;{' '}
          <Link to="/login" className="text-blue-600 dark:text-blue-400 hover:underline">
            Back to Sign In
          </Link>
        </p>
      </div>
    </div>
  );
}
